import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, LeafIcon, AtomIcon, SunIcon } from 'lucide-react';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function SolutionSection() {
  return <section id="libro" className="relative w-full py-24 sm:py-32" data-id="element-256">
      <div className="max-w-6xl mx-auto px-6" data-id="element-257">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-16" data-id="element-258">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-259">
            Los pilares del libro
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white" data-id="element-260">
            El mecanismo de lo sagrado
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-261">
          {/* Card 1 — Pineal Gland */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-262">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-263" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-8 sm:p-10 h-full overflow-hidden" data-id="element-264">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-gold/40 via-gold/10 to-transparent" data-id="element-265" />
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-6" data-id="element-266">
                <EyeIcon className="w-6 h-6 text-gold" data-id="element-267" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-268">
                La Glándula Pineal
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-269">
                Tu antena biológica hacia dimensiones superiores. El órgano que
                la ciencia olvidó y que las tradiciones antiguas siempre
                veneraron.
              </p>
            </div>
          </motion.div>

          {/* Card 2 — Ayahuasca */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-270">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-electric/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-271" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-8 sm:p-10 h-full overflow-hidden" data-id="element-272">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-purple-electric/40 via-purple-electric/10 to-transparent" data-id="element-273" />
              <div className="w-12 h-12 rounded-lg bg-purple-electric/10 flex items-center justify-center mb-6" data-id="element-274">
                <LeafIcon className="w-6 h-6 text-purple-electric" data-id="element-275" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-276">
                Ayahuasca
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-277">
                El lenguaje vegetal que habla directamente al alma. Una
                tecnología ancestral de sanación y revelación que trasciende el
                tiempo.
              </p>
            </div>
          </motion.div>

          {/* Card 3 — Quantum Physics */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-278">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-astral/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-279" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-8 sm:p-10 h-full overflow-hidden" data-id="element-280">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-astral/40 via-astral/10 to-transparent" data-id="element-281" />
              <div className="w-12 h-12 rounded-lg bg-astral/10 flex items-center justify-center mb-6" data-id="element-282">
                <AtomIcon className="w-6 h-6 text-astral" data-id="element-283" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-284">
                Física Cuántica
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-285">
                La ciencia de lo invisible que valida lo sagrado. Donde la
                materia se disuelve y la conciencia se revela como el fundamento
                de todo.
              </p>
            </div>
          </motion.div>

          {/* Card 4 — Integration */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-286">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-287" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-8 sm:p-10 h-full overflow-hidden" data-id="element-288">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-gold/40 via-gold/10 to-transparent" data-id="element-289" />
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-6" data-id="element-290">
                <SunIcon className="w-6 h-6 text-gold" data-id="element-291" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-292">
                Integración
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-293">
                Cómo vivir despierto en un mundo dormido. Herramientas prácticas
                para incorporar la experiencia expandida en tu vida cotidiana.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}